// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useEffect } from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import { useSessionGuardStore } from '../../stores/sessionGuardStore';
import { useIdleActivityTracker } from '../../hooks/useIdleActivityTracker';
import { useIdleWarning } from '../../hooks/useIdleWarning';
import { useAbsoluteSessionLimit } from '../../hooks/useAbsoluteSessionLimit';
import IdleWarningModal from './IdleWarningModal';
import LockOverlay from './LockOverlay';

/**
 * Root-level session-expiry orchestrator (ADO 25798–25803). Mounted once in
 * `App.tsx`, above the `/admin-center/*` vs `/*` split, so idle timeout,
 * the 12h absolute limit, and the lock overlay apply identically to both
 * shells without either needing to know this exists.
 *
 * Renders nothing beyond the one-time kiosk-flag check while unauthenticated
 * (e.g. on `/login`) or once a browser has been marked as a wall display.
 */
function SessionGuard() {
  const isAuthenticated = useIsAuthenticated();
  const kiosk = useSessionGuardStore(s => s.kiosk);
  const setKiosk = useSessionGuardStore(s => s.setKiosk);
  const locked = useSessionGuardStore(s => s.locked);
  const lockReason = useSessionGuardStore(s => s.lockReason);

  // One-time: a `?kiosk=1` visit marks this browser as a permanent wall
  // display. No UI ever unsets it.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('kiosk') === '1') setKiosk(true);
  }, [setKiosk]);

  const guardEnabled = isAuthenticated && !kiosk;

  useIdleActivityTracker(guardEnabled);
  const { warningVisible, secondsLeft } = useIdleWarning(guardEnabled);
  useAbsoluteSessionLimit(guardEnabled);

  if (!guardEnabled) return null;

  return (
    <>
      {warningVisible && !locked && <IdleWarningModal secondsLeft={secondsLeft} />}
      {locked && <LockOverlay reason={lockReason} />}
    </>
  );
}

export default SessionGuard;
